import ClickAwayListener from "@mui/material/ClickAwayListener";
import isEqual from "lodash/isEqual";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";

import ClearIcon from "./icon-clear.svg";
import {
  Box,
  ClearButton,
  FieldBoxActive,
  FieldBoxInactive,
  FieldIconActive,
  FieldIconInactive,
  FieldTextActive,
  FieldTextInactive,
  Menu,
  MenuDivider,
  MenuItem,
  Wrapper,
} from "./styles";

type DropdownFieldOption<T> = {
  count?: string;
  text: string;
  value: T;
};

type Props<T, TOption extends DropdownFieldOption<T>> = {
  className?: string;
  icon: ReactNode;
  onOptionSelect: (option: TOption) => void;
  options: TOption[];
  placeholder: string;
  selectedOptionValue?: T;
  withCount?: boolean;
};

export const DropdownField = <T, TOption extends DropdownFieldOption<T> = DropdownFieldOption<T>>({
  className,
  icon,
  onOptionSelect,
  options,
  placeholder,
  selectedOptionValue,
  withCount = false,
}: Props<T, TOption>) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedText = useMemo(() => {
    if (selectedOptionValue === undefined) return;
    const found = options.find(option => isEqual(option.value, selectedOptionValue))?.text;
    if (!found || found === "All") return;
    return found;
  }, [options, selectedOptionValue]);

  const isFieldSelected = useMemo(
    () =>
      selectedOptionValue !== undefined &&
      options.length > 0 &&
      !isEqual(selectedOptionValue, options[0].value),
    [options, selectedOptionValue],
  );

  const FieldBox = isFieldSelected ? FieldBoxActive : FieldBoxInactive;
  const FieldIcon = isFieldSelected ? FieldIconActive : FieldIconInactive;
  const FieldText = isFieldSelected ? FieldTextActive : FieldTextInactive;

  return (
    <Wrapper className={className}>
      <ClickAwayListener onClickAway={() => setIsOpen(false)}>
        <Box>
          <FieldBox
            $isOpen={isOpen}
            aria-expanded={isOpen ? "true" : undefined}
            aria-haspopup="listbox"
            onClick={() => setIsOpen(current => !current)}
            type="button"
          >
            <FieldIcon>{icon}</FieldIcon>
            <FieldText>{selectedText ?? placeholder}</FieldText>
            {isFieldSelected && (
              <ClearButton
                aria-label="Clear"
                onClick={event => {
                  event.stopPropagation();
                  const allOption = options[0];
                  if (allOption) onOptionSelect(allOption);
                }}
              >
                <ClearIcon />
              </ClearButton>
            )}
          </FieldBox>
          {isOpen && (
            <Menu role="listbox">
              <MenuDivider />
              {options.map((option, index) => (
                <MenuItem
                  $isAll={index === 0}
                  $isZero={option.count === "0"}
                  key={index}
                  onClick={() => {
                    setIsOpen(false);
                    onOptionSelect(option);
                  }}
                  role="option"
                >
                  <span>{option.text}</span>
                  {withCount && option.count && <em>{option.count}</em>}
                </MenuItem>
              ))}
            </Menu>
          )}
        </Box>
      </ClickAwayListener>
    </Wrapper>
  );
};

DropdownField.displayName = "DropdownField";
